import { Injectable, inject } from '@angular/core';
import NutrientViewer from '@nutrient-sdk/viewer';
import { firstValueFrom } from 'rxjs';

import { Permission, Scope } from '@models/permission';
import { TranslationPlatform } from '@models/system';
import { LocaleService } from '@shared/services';
import { TranslationService } from '@shared/services/translation.service';
import { environment } from 'src/environments/environment';
import { DocumentLinkCustomData, Nutrient, NutrientAnnotationTooltipItem, NutrientToolbarItem } from './nutrient.model';

type MarkupCallbacks = {
  showDocumentLinkDialog: () => void;
  placeIcon: () => void;
  annotationTooltipCallback?: (annotation: Nutrient.AnnotationsUnion) => NutrientAnnotationTooltipItem[];
  permissions?: { permissionId: Permission; scopeId: Scope }[];
  canUpdateAnnotations?: boolean;
  isCurrent?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class NutrientService {
  private localeService = inject(LocaleService);
  private translationService = inject(TranslationService);

  /** Nutrient load config for view only
   * @param document - The document URL to load
   */
  async nutrientViewConfig(document: string | ArrayBuffer): Promise<Nutrient.Configuration> {
    if (!NutrientViewer.I18n.locales.includes(this.localeService.locale)) {
      await this.setLocale(this.localeService.locale);
    }
    return {
      ...this.buildBaseConfig(),
      document,
      toolbarItems: this.getToolbarItems(false),
      editableAnnotationTypes: [],
      initialViewState: new NutrientViewer.ViewState({ zoom: NutrientViewer.ZoomMode.FIT_TO_VIEWPORT }),
    };
  }

  /** Nutrient load config for edit/markup mode using PSPDFKit Instant
   * @param documentId - The PSPDFKit Instant document ID
   * @param jwt - JWT token for authentication
   * @param callbacks - Custom toolbar action callbacks
   */
  async nutrientMarkupConfig(
    documentId: string,
    jwt: string,
    callbacks: MarkupCallbacks,
  ): Promise<Nutrient.Configuration> {
    if (!NutrientViewer.I18n.locales.includes(this.localeService.locale)) {
      await this.setLocale(this.localeService.locale);
    }
    const { permissions, canUpdateAnnotations = false, isCurrent = true } = callbacks;
    return {
      ...this.buildBaseConfig(),
      serverUrl: environment.pspdfkitUrl,
      documentId,
      authPayload: { jwt },
      instant: true,
      isEditableAnnotation: () => isCurrent && canUpdateAnnotations,
      toolbarItems: this.getToolbarItems(isCurrent, callbacks),
      initialViewState: new NutrientViewer.ViewState({ enableAnnotationToolbar: true }),
      annotationToolbarItems: (
        annotation: Nutrient.AnnotationsUnion,
        { defaultAnnotationToolbarItems }: { defaultAnnotationToolbarItems: { type: string }[] },
      ) => {
        let items = defaultAnnotationToolbarItems;
        // Opacity does not update live on image (raster) annotations in Instant mode because they are
        // server-rendered, unlike vector annotations. Hide the opacity control for all image annotations
        // so users don't get a control that appears broken; it stays where it works live (ink/shape/text).
        if (annotation instanceof NutrientViewer.Annotations.ImageAnnotation) {
          items = items.filter(item => item.type !== 'opacity');
        }
        const customData = annotation?.customData as DocumentLinkCustomData;
        if (
          customData?.annotationType === 'documentLink' &&
          !permissions?.some(p => p.permissionId === Permission.Delete)
        ) {
          items = items.filter(item => item.type !== 'delete');
        }
        return items;
      },
      ...(callbacks.annotationTooltipCallback
        ? { annotationTooltipCallback: callbacks.annotationTooltipCallback }
        : {}),
    } as unknown as Nutrient.Configuration;
  }

  private buildBaseConfig() {
    return {
      baseUrl: `${window.location.protocol}//${window.location.host}/src/assets/`,
      container: '#nutrient-container',
      licenseKey: environment.pspdfkitLicenseKey,
      locale: this.localeService.locale,
    };
  }

  private getToolbarItems(isMarkup: boolean, callbacks?: MarkupCallbacks) {
    const items = this.buildToolbarItems(callbacks);
    return isMarkup ? items.filter(item => !item.isViewer) : items.filter(item => !item.isMarkup);
  }

  private buildToolbarItems(callbacks?: MarkupCallbacks): NutrientToolbarItem[] {
    const messages = NutrientViewer.I18n.messages[this.localeService.locale] || {};
    return [
      { type: 'sidebar-thumbnails', isMarkup: false },
      { type: 'sidebar-document-outline', isMarkup: false },
      { type: 'sidebar-annotations', isMarkup: true },
      { type: 'sidebar-bookmarks', isMarkup: true },
      { type: 'pager', isMarkup: false },
      { type: 'layout-config', isMarkup: false },
      { type: 'zoom-mode', isMarkup: false },
      { type: 'zoom-out', isMarkup: false },
      { type: 'zoom-in', isMarkup: false },
      { type: 'pan', isViewer: true },
      { type: 'document-editor', isMarkup: true },
      { type: 'spacer', isMarkup: false },
      {
        type: 'custom',
        id: 'documents',
        title: messages['documents'],
        icon: '/Content/pspdfkit/icons/cipo_documents.png',
        onPress: callbacks?.showDocumentLinkDialog ?? (() => {}),
        dropdownGroup: 'cipo-dropdown-group',
        selected: false,
        isMarkup: true,
      },
      {
        type: 'custom',
        id: 'addAssociation',
        title: messages['addAssociation'],
        icon: '/Content/pspdfkit/icons/add_association_1.png',
        onPress: callbacks?.placeIcon ?? (() => {}),
        dropdownGroup: 'cipo-dropdown-group',
        selected: false,
        isMarkup: true,
      },
      { type: 'annotate', isMarkup: true },
      { type: 'ink', isMarkup: true },
      { type: 'highlighter', isMarkup: true },
      { type: 'text-highlighter', isMarkup: true },
      { type: 'ink-eraser', isMarkup: true },
      { type: 'line', isMarkup: true },
      { type: 'arrow', isMarkup: true },
      { type: 'rectangle', isMarkup: true },
      { type: 'ellipse', isMarkup: true },
      { type: 'polygon', isMarkup: true },
      { type: 'polyline', isMarkup: true },
      { type: 'note', isMarkup: true },
      { type: 'text', isMarkup: true },
      { type: 'image', isMarkup: true },
      { type: 'print', isMarkup: false },
      { type: 'search', isMarkup: false },
    ];
  }

  private async setLocale(locale: string) {
    const bundles = await firstValueFrom(
      this.translationService.getBundles(locale, [TranslationPlatform.NutrientAngular]),
    );
    const translations = bundles.NutrientAngular ?? {};
    NutrientViewer.I18n.locales.push(locale);
    NutrientViewer.I18n.messages[locale] = translations;
  }
}
